import { useState, useRef, useCallback, useMemo, useEffect, type ReactNode } from 'react';
import { legalityNote, useRules } from '../utils/legality';
import { Frown, Play, Volume2, VolumeX } from 'lucide-react';
import PlayerHeader from './PlayerHeader';
import { detectPlatform, getEmbedUrl, needsServerExtraction, isYouTubeShort, getThumbnailUrl } from '../utils/video';
import type { Technique, StatusMap, BeltRank } from '../types';
import { TechniqueStatus } from '../types';
import { categories } from '../data/categories';
import { previewVideoUrl, techniqueIdFromPreviewUrl } from '../api/preview';
import { previewExhausted, recordPreviewPlay } from '../previewGate';

const API_BASE = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || '';

interface FeedPlayerProps {
  techniques: Technique[];
  statusMap: StatusMap;
  onClose: () => void;
  /** Star / un-star the clip on screen. Toggles: the same call takes it back
   *  out of Favorites. */
  onToggleStar: (techniqueId: string) => void;
  /** Render inline (inside the panel) instead of as an overlay */
  inline?: boolean;
  /** Mute all videos (used in promo preview) */
  muted?: boolean;
  /** Called when the user pulls down at the very first item — used by the
   *  Discover tab to scroll back up to its intro instead of rubber-banding. */
  onExitTop?: () => void;
  /** Intro sheet shown over the top of the first clip. The clip stays parked on
   *  its first frame until the user swipes the sheet up, which dismisses it and
   *  starts playback. Omit for feeds that should play straight away. */
  intro?: ReactNode;
  /** Restrict the feed by these filters (shared Library/Discover filter). Empty/undefined = no constraint. */
  filterCategoryIds?: string[];
  filterTypeIds?: string[];
  filterRanks?: BeltRank[];
  /** Free preview: count each clip against the ten free plays. */
  guest?: boolean;
  /** Guest tried to swipe past their last free play — time for the wall. */
  onPreviewExhausted?: () => void;
}

// Whether a technique can appear in the Discover feed: has a playable video,
// isn't a non-Shorts YouTube clip (wrong aspect ratio), and isn't already
// marked. Shared with the Discover primer so its chips never lead to an empty
// feed.
export function isDiscoverable(t: Technique, statusMap: StatusMap): boolean {
  if (!t.instagramUrl) return false;
  const platform = detectPlatform(t.instagramUrl);
  if (platform === 'youtube' && !isYouTubeShort(t.instagramUrl)) return false;
  const s = statusMap[t.id];
  return !s || s === TechniqueStatus.Unchecked;
}


// Module-level cache: source URL → resolved direct video URL
const videoUrlCache = new Map<string, string>();

async function fetchDirectUrl(url: string): Promise<string> {
  const cached = videoUrlCache.get(url);
  if (cached) return cached;

  // Free preview: a guest's techniques carry an opaque handle instead of the
  // source post, so the URL is resolved by id through the preview endpoint.
  // The source list never reaches a signed-out client in bulk.
  const previewId = techniqueIdFromPreviewUrl(url);
  if (previewId) {
    const resolved = await previewVideoUrl(previewId);
    if (!resolved) throw new Error('broken');
    videoUrlCache.set(url, resolved);
    return resolved;
  }

  const base = API_BASE
    ? `${API_BASE}?url=${encodeURIComponent(url)}`
    : `/api/ig-video?url=${encodeURIComponent(url)}`;

  const { auth } = await import('../firebase');
  const token = await auth.currentUser?.getIdToken();
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  // Every platform takes the same path: the function returns a CDN URL and the
  // <video> element fetches the bytes itself. TikTok used to be streamed through
  // the function as a blob; that put video on our servers and is gone for good
  // (see the hard rule in functions/SCRAPING_POLICY.md).
  const resp = await fetch(base, { headers });
  if (resp.status === 401 || resp.status === 403) throw new Error('auth');
  if (resp.status === 429) throw new Error('rate');
  const data = await resp.json();
  if (!data.videoUrl) throw new Error('broken');
  const result = data.videoUrl as string;

  videoUrlCache.set(url, result);
  return result;
}

// The technique chosen during preload, so the feed can open on the video we
// already warmed instead of a fresh random pick.
let preloadedId: string | null = null;

// Warm the first Discover video before the user opens the tab: pick a
// discoverable technique, resolve its direct video URL (the slow server
// extraction), and kick off media buffering so playback is instant on open.
export async function preloadFirstDiscover(techniques: Technique[], statusMap: StatusMap): Promise<void> {
  if (preloadedId) return;
  const pool = techniques.filter((t) => isDiscoverable(t, statusMap));
  if (pool.length === 0) return;
  const pick = pool[Math.floor(Math.random() * pool.length)];
  preloadedId = pick.id;
  const url = pick.instagramUrl!;
  const platform = detectPlatform(url);
  // Embeds (YouTube etc.) load via iframe — there's no direct URL to warm.
  if (platform && !needsServerExtraction(platform)) return;
  try {
    const direct = await fetchDirectUrl(url);
    // Start buffering the media so the first frame is ready when opened.
    const v = document.createElement('video');
    v.preload = 'auto';
    v.muted = true;
    v.src = direct;
  } catch {
    preloadedId = null; // let the feed fall back to a fresh pick
  }
}

// A cross-origin iframe never lets its events out, so a swipe over a YouTube
// embed can't reach this feed's handlers. The fix is a transparent layer over
// the player that catches the gesture in our own document; since that layer
// also covers YouTube's controls, it drives the player through the iframe API
// instead — which the embed only accepts with enablejsapi on its URL.
//
// The clip starts itself, like every other clip in the feed. It has to start
// muted: audible autoplay is blocked outright on iOS and gated on desktop, and
// a blocked start leaves the slide sitting on a play button. Sound is one tap
// away on the speaker button. The two slides waiting either side start too and
// are paused by command a moment later — which keeps the URL constant, so
// swiping resumes a loaded player instead of reloading the frame.
function embedSrc(embedUrl: string): string {
  const sep = embedUrl.includes('?') ? '&' : '?';
  return `${embedUrl}${sep}enablejsapi=1&playsinline=1&mute=1&autoplay=1`;
}

// The black bar drawn over the top of the embed. Our header sits on it, and it
// hides the title and channel the player paints across its own top edge — plus
// the strip the player keeps above its own picture. Which is the point: the
// clip runs full-bleed to the nav bar underneath, rather than being pushed down
// the frame to keep clear of anything.
const YT_TOP_BAR = 100;

// Drive an embedded player (play/pause/mute) from our own document.
function ytCommand(frame: HTMLIFrameElement | null, func: string) {
  frame?.contentWindow?.postMessage(
    JSON.stringify({ event: 'command', func, args: [] }),
    'https://www.youtube.com',
  );
}

function applyEmbedState(frame: HTMLIFrameElement | null, want: { play: boolean; muted: boolean }) {
  ytCommand(frame, want.play ? 'playVideo' : 'pauseVideo');
  ytCommand(frame, want.muted ? 'mute' : 'unMute');
}

// ─── Video renderer for a single feed item ───
function FeedVideo({ url, thumbnail, muted, soundOn, onToggleSound, hold, onNext, active }: { url: string; thumbnail?: string; muted?: boolean; soundOn?: boolean; onToggleSound?: () => void; hold?: boolean; onNext?: () => void; active?: boolean }) {
  const platform = detectPlatform(url);
  const useEmbed = platform ? !needsServerExtraction(platform) : false;
  const embedUrl = useEmbed ? getEmbedUrl(url) : null;
  // Embeds only when that platform's kill switch is on (see isEmbedOnly).
  // Their iframe is a separate document: touches inside it never reach this
  // feed's swipe handlers, and it won't autoplay. So the slide shows the stored
  // thumbnail first — swiping works because it's an image — and only mounts the
  // iframe once the user taps. YouTube is excluded; it embeds as it always has,
  // and keeps its swipe through the gesture layer below instead.
  const posterFirst = useEmbed && platform !== 'youtube';
  const [activated, setActivated] = useState(false);
  // YouTube shows its player up front, so it's the embed that needs the
  // gesture layer to stay swipeable.
  const shieldEmbed = platform === 'youtube';
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // What we've asked the embedded player for, so a tap toggles the right way
  // and a command that arrived too early can be sent again (see below).
  const embedWanted = useRef({ play: false, muted: true });
  const posterUrl = posterFirst ? getThumbnailUrl(url, thumbnail) : null;
  const showPoster = posterFirst && !activated;
  const isPortraitYT = platform === 'youtube' && isYouTubeShort(url);
  const defaultRatio = platform === 'youtube' ? (isPortraitYT ? 9 / 16 : 16 / 9)
    : (platform === 'tiktok' || platform === 'instagram') ? 9 / 16 : null;

  const [state, setState] = useState<{ videoUrl: string | null; loading: boolean; error: string | null; forUrl: string }>(() => {
    const cached = !useEmbed ? videoUrlCache.get(url) ?? null : null;
    return { videoUrl: cached, loading: !useEmbed && !cached, error: null, forUrl: url };
  });
  const [aspectRatio, setAspectRatio] = useState<number | null>(defaultRatio);
  // Hidden until the <video> element has actually painted its first frame —
  // prevents Firefox's brief white flash while the element initialises.
  const [videoReady, setVideoReady] = useState(false);
  // Custom scrubber state: native <video controls> only renders a tap-to-reveal
  // play/pause overlay inside this swipe feed (the bottom scrubber never shows),
  // so we draw our own progress bar and own play/pause on tap.
  const [progress, setProgress] = useState(0); // 0..1 of duration
  const [paused, setPaused] = useState(false);
  const videoElRef = useRef<HTMLVideoElement>(null);
  const retried = useRef(false);
  // Distinguish a stationary tap (toggle play/pause) from a swipe (feed nav,
  // handled by the parent viewport) by how far the pointer moved.
  const tapStart = useRef<{ x: number; y: number } | null>(null);
  const seeking = useRef(false);

  // `hold` keeps the clip parked on its first frame (the Discover intro sheet
  // is covering it). Releasing it starts playback — the swipe that dismissed
  // the sheet counts as the user gesture iOS wants before playing.
  useEffect(() => {
    const v = videoElRef.current;
    if (!v) return;
    if (hold) v.pause();
    else if (v.paused) v.play().catch(() => {});
  }, [hold, state.videoUrl]);

  const seekToClientX = (clientX: number, bar: HTMLElement) => {
    const v = videoElRef.current;
    if (!v || !v.duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    v.currentTime = ratio * v.duration;
    setProgress(ratio);
  };

  // Reset state when URL changes (component reused across slides)
  if (state.forUrl !== url) {
    const cached = !useEmbed ? videoUrlCache.get(url) ?? null : null;
    setState({ videoUrl: cached, loading: !useEmbed && !cached, error: null, forUrl: url });
    setVideoReady(false);
    setActivated(false);
  }

  // React doesn't reliably update the muted attribute on <video>
  useEffect(() => {
    if (videoElRef.current) videoElRef.current.muted = !!muted;
  }, [muted]);

  // What this player should be doing: only the slide you're looking at runs
  // (and not while the intro sheet holds it), and sound follows the feed's
  // speaker button. Everything loads muted, so resuming by command is allowed
  // on every browser — it's unmuting that browsers guard.
  useEffect(() => {
    if (!shieldEmbed) return;
    embedWanted.current = { play: !!active && !hold, muted: !!muted };
    applyEmbedState(iframeRef.current, embedWanted.current);
  }, [shieldEmbed, active, hold, muted]);

  // Commands posted before the player finishes loading are dropped, and a clip
  // can also run out on its own — so each status message it sends is a chance
  // to put it back where it belongs. Only on a mismatch: it reports often, and
  // re-sending a command it's already obeying just makes it stutter. Playing it
  // again from a finished state is what loops the clip, like a direct one.
  useEffect(() => {
    if (!shieldEmbed) return;
    const onMessage = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (data?.event === 'onReady') {
          applyEmbedState(iframeRef.current, embedWanted.current);
          return;
        }
        const info = data?.info?.playerState !== undefined || data?.info?.muted !== undefined
          ? data.info
          : data?.event === 'onStateChange' ? { playerState: data.info } : null;
        if (!info) return;
        const want = embedWanted.current;
        // 1 playing, 3 buffering — anything else is stopped one way or another.
        if (typeof info.playerState === 'number' && (info.playerState === 1 || info.playerState === 3) !== want.play) {
          ytCommand(iframeRef.current, want.play ? 'playVideo' : 'pauseVideo');
        }
        if (typeof info.muted === 'boolean' && info.muted !== want.muted) {
          ytCommand(iframeRef.current, want.muted ? 'mute' : 'unMute');
        }
      } catch { /* not a player message */ }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [shieldEmbed]);

  useEffect(() => {
    if (useEmbed || state.videoUrl) return;
    retried.current = false;
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchDirectUrl(url);
        if (!cancelled) setState((s) => ({ ...s, videoUrl: result, loading: false, error: null }));
      } catch (e) {
        if (!cancelled) setState((s) => ({ ...s, videoUrl: null, loading: false, error: e instanceof Error ? e.message : 'broken' }));
      }
    })();
    return () => { cancelled = true; };
  }, [url, useEmbed, state.videoUrl]);

  const { videoUrl, loading, error } = state;

  return (
    <div
      className={`feed-slide${useEmbed ? ' feed-slide--embed' : ''}${shieldEmbed ? ' feed-slide--yt' : ''}`}
      style={aspectRatio ? { aspectRatio: `${aspectRatio}` } : undefined}
    >
      {loading && (
        <div className="pip-status">
          <svg className="pip-spinner" width="32" height="32" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.15"/>
            <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
      )}
      {error && (
        <div className="pip-status pip-status--error">
          <Frown size={32} />
          <span>{error === 'rate' ? 'Too many requests' : error === 'auth' ? 'Sign in again' : 'This link is broken'}</span>
        </div>
      )}
      {showPoster && (
        <div
          className="feed-poster"
          onPointerDown={(e) => { tapStart.current = { x: e.clientX, y: e.clientY }; }}
          onPointerUp={(e) => {
            const start = tapStart.current;
            tapStart.current = null;
            // Anything but a near-stationary tap is the parent's swipe.
            if (!start) return;
            if (Math.abs(e.clientX - start.x) > 10 || Math.abs(e.clientY - start.y) > 10) return;
            setActivated(true);
          }}
        >
          {posterUrl && <img src={posterUrl} alt="" draggable={false} />}
          <div className="feed-poster-play" aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          </div>
        </div>
      )}
      {embedUrl && !showPoster && (
        // The player letterboxes its 9:16 clip inside whatever box we give it,
        // so a slide wider than that showed pillarbox bars. Size the iframe to
        // the clip's own shape and crop the overflow instead — the same cover
        // fit the direct <video> path gets. Container units need a sized
        // container, which is what this wrapper is for.
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            zIndex: 1,
            overflow: 'hidden',
            containerType: isPortraitYT ? 'size' : undefined,
          }}
        >
          <iframe
            ref={iframeRef}
            src={shieldEmbed ? embedSrc(embedUrl) : embedUrl}
            style={isPortraitYT ? {
              position: 'absolute',
              // Top-anchored: the bar above covers the player's own title, and
              // the overflow all comes off the bottom, where nothing but its
              // control bar lives — and the speaker moved up to our header.
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'max(100cqw, calc(100cqh * 9 / 16))',
              height: 'max(100cqh, calc(100cqw * 16 / 9))',
              border: 'none',
            } : { width: '100%', height: '100%', border: 'none' }}
            allowFullScreen
            allow="autoplay; encrypted-media"
            // Registering as a listener is what makes the player post its state
            // changes back to us.
            onLoad={() => {
              if (!shieldEmbed) return;
              iframeRef.current?.contentWindow?.postMessage(
                JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }),
                'https://www.youtube.com',
              );
            }}
          />
        </div>
      )}
      {embedUrl && !showPoster && shieldEmbed && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: YT_TOP_BAR, background: '#000', zIndex: 2 }} />
      )}
      {/* Gesture layer over the player: swipes and wheel events land in our own
          document and bubble to the feed viewport, a stationary tap plays or
          pauses the embed. */}
      {embedUrl && !showPoster && shieldEmbed && (
        <div
          style={{ position: 'absolute', inset: 0, zIndex: 3, cursor: 'pointer' }}
          onPointerDown={(e) => { tapStart.current = { x: e.clientX, y: e.clientY }; }}
          onPointerUp={(e) => {
            const start = tapStart.current;
            tapStart.current = null;
            // Anything but a near-stationary tap is the parent's swipe.
            if (!start) return;
            if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > 10) return;
            embedWanted.current.play = !embedWanted.current.play;
            applyEmbedState(iframeRef.current, embedWanted.current);
          }}
        />
      )}
      {/* With the iframe mounted it swallows the swipe, so advancing needs an
          explicit control. Only shown once an embed is actually playing. */}
      {posterFirst && activated && onNext && (
        <button
          type="button"
          className="feed-embed-next"
          aria-label="Next technique"
          onClick={onNext}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
        </button>
      )}
      {videoUrl && !embedUrl && (
        <video
          ref={videoElRef}
          src={videoUrl}
          autoPlay={!hold}
          // With autoplay held off we still want the first frame decoded, so
          // the sheet sits over a real still rather than a black rectangle.
          preload={hold ? 'auto' : undefined}
          loop
          playsInline
          muted={muted}
          // Stay invisible until we have actual video pixels — prevents the
          // white flash Firefox paints while a <video> element initialises.
          style={{ opacity: videoReady ? 1 : 0 }}
          onTimeUpdate={(e) => {
            const v = e.currentTarget;
            if (v.duration) setProgress(v.currentTime / v.duration);
          }}
          onPlay={() => setPaused(false)}
          onPause={() => setPaused(true)}
          // Tap (not swipe) toggles play/pause; the parent viewport handles
          // swipes, so only treat a near-stationary pointer as a tap.
          onPointerDown={(e) => { tapStart.current = { x: e.clientX, y: e.clientY }; }}
          onPointerUp={(e) => {
            const start = tapStart.current;
            tapStart.current = null;
            if (!start) return;
            if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > 10) return;
            const v = e.currentTarget;
            if (v.paused) v.play().catch(() => {}); else v.pause();
          }}
          onLoadedData={() => setVideoReady(true)}
          onPlaying={() => setVideoReady(true)}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            if (v.videoWidth && v.videoHeight) setAspectRatio(v.videoWidth / v.videoHeight);
          }}
          onError={async () => {
            if (retried.current) { setState((s) => ({ ...s, error: 'broken' })); return; }
            retried.current = true;
            try {
              const fresh = await fetchDirectUrl(url);
              setState((s) => ({ ...s, videoUrl: fresh, loading: false, error: null }));
            } catch { setState((s) => ({ ...s, error: 'broken' })); }
          }}
        />
      )}
      {videoUrl && !embedUrl && videoReady && (
        <>
          {paused && (
            <div className="feed-play-overlay" aria-hidden="true">
              <Play size={56} fill="currentColor" />
            </div>
          )}
          {/* Custom scrubber pinned to the bottom of the frame. Touch events
              stopPropagation so a horizontal seek-drag doesn't trigger the
              parent viewport's vertical swipe-to-next-video gesture. */}
          <div
            className="feed-seek"
            onPointerDown={(e) => {
              seeking.current = true;
              e.currentTarget.setPointerCapture(e.pointerId);
              seekToClientX(e.clientX, e.currentTarget);
            }}
            onPointerMove={(e) => { if (seeking.current) seekToClientX(e.clientX, e.currentTarget); }}
            onPointerUp={() => { seeking.current = false; }}
            onPointerCancel={() => { seeking.current = false; }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <div className="feed-seek-fill" style={{ width: `${progress * 100}%` }} />
          </div>
        </>
      )}
      {videoUrl && !embedUrl && onToggleSound && (
        <button
          className="feed-mute-btn"
          // Flip the element's muted flag synchronously inside the gesture
          // (iOS only honours unmute from a real user action), then lift the
          // choice to the feed so it sticks across swipes.
          onClick={(e) => {
            e.stopPropagation();
            const v = videoElRef.current;
            const next = !soundOn;
            if (v) { v.muted = !next; if (next) v.play().catch(() => {}); }
            onToggleSound();
          }}
          aria-label={soundOn ? 'Mute' : 'Unmute'}
        >
          {soundOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
        </button>
      )}
    </div>
  );
}

// Swipe thresholds
const SWIPE_THRESHOLD_RATIO = 0.25;
const SWIPE_THRESHOLD_MAX = 80;
const VELOCITY_THRESHOLD = 0.4;
const ANIM_MS = 280;

// ─── Main feed player ───
export default function FeedPlayer({ techniques, statusMap, onClose, onToggleStar, inline, muted: allMuted, onExitTop, intro, filterCategoryIds, filterTypeIds, filterRanks, guest, onPreviewExhausted }: FeedPlayerProps) {
  const rules = useRules();
  // All techniques with video — pool for picking random next
  const pool = useMemo(() =>
    techniques.filter((t) =>
      isDiscoverable(t, statusMap) &&
      (!filterCategoryIds?.length || filterCategoryIds.includes(t.categoryId)) &&
      (!filterTypeIds?.length || filterTypeIds.includes(t.type)) &&
      (!filterRanks?.length || filterRanks.includes(t.tier))
    ),
    [techniques, statusMap, filterCategoryIds, filterTypeIds, filterRanks]
  );

  // Lookup by ID so we can find techniques even after they leave the pool
  const techById = useMemo(() => {
    const map = new Map<string, Technique>();
    for (const t of techniques) if (t.instagramUrl) map.set(t.id, t);
    return map;
  }, [techniques]);

  // The feed is shuffled, not repeating: every id handed out is recorded, and
  // picks only come from what's still unseen. A duplicate is only possible once
  // the whole pool has been through, which starts a fresh lap. `recent` holds
  // the tail of that order so the lap boundary doesn't put a clip back on
  // screen right after it played.
  const [served] = useState<Set<string>>(() => new Set());
  const [recent] = useState<Set<string>>(() => new Set());

  const pickRandom = useCallback((excludeId?: string) => {
    if (pool.length === 0) return null;
    let unseen = pool.filter((t) => t.id !== excludeId && !served.has(t.id));
    if (unseen.length === 0) {
      // Whole pool seen — the next lap runs over everything again. The clip on
      // screen only sits out of this one pick; it stays eligible for the rest
      // of the lap.
      served.clear();
      unseen = pool.filter((t) => t.id !== excludeId);
    }
    // A pool of one has no "next": handing the current clip back as next put
    // the same technique in two slides with one key, and React thrashed the
    // video between them (it loaded, then reloaded, forever).
    if (unseen.length === 0) return null;
    // Straight after a lap rolls over, everything is unseen again, including
    // the handful just watched — hold those back while there's anything else.
    const fresh = unseen.filter((t) => !recent.has(t.id));
    const candidates = fresh.length > 0 ? fresh : unseen;
    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    served.add(pick.id);
    // A Set keeps insertion order, so the oldest entry is the first one out.
    // The tail stays short enough that a small pool still has somewhere to go.
    recent.add(pick.id);
    while (recent.size > Math.min(3, pool.length - 1)) {
      const oldest = recent.values().next().value;
      if (oldest === undefined) break;
      recent.delete(oldest);
    }
    return pick;
  }, [pool, served, recent]);

  const [currentId, setCurrentId] = useState<string | null>(() => {
    // Open on the preloaded video if it's still in the current pool.
    if (preloadedId && pool.some((t) => t.id === preloadedId)) {
      served.add(preloadedId);
      return preloadedId;
    }
    return pickRandom()?.id ?? null;
  });
  const [nextId, setNextId] = useState<string | null>(() => pickRandom(currentId ?? undefined)?.id ?? null);
  const [historyIds, setHistoryIds] = useState<string[]>([]);

  // ── Intro sheet ──
  // Sits over the first clip and holds playback until swiped up. Once gone it
  // stays gone for the life of the feed.
  const [introGone, setIntroGone] = useState(false);
  const showIntro = !!intro && !introGone;
  // Live finger offset while dragging the sheet, and the flag that runs the
  // slide-off transition before unmounting it.
  const [introOffset, setIntroOffset] = useState(0);
  const [introLeaving, setIntroLeaving] = useState(false);
  const introDragFrom = useRef<number | null>(null);
  // Mirrors introOffset so drag-end can read the committed distance directly.
  // Deciding from inside a setState updater doesn't work — updaters have to be
  // pure, so a dismiss triggered in one isn't reliably applied.
  const introOffsetRef = useRef(0);

  const introLayerRef = useRef<HTMLDivElement>(null);
  const introDismissing = useRef(false);

  const dismissIntro = useCallback(() => {
    if (introDismissing.current) return; // drag-end and click can both land
    introDismissing.current = true;
    // The first clip and its follow-up were picked when the feed mounted, before
    // any filters were chosen on the intro. Re-pick whichever of them the
    // filters have since excluded so "Start watching" actually starts on a
    // matching clip. Done here rather than on every pool change, because the
    // pool also shrinks when the current clip is marked practiced and that
    // must not yank the video away mid-watch.
    if (pool.length > 0) {
      const inPool = (id: string | null) => !!id && pool.some((t) => t.id === id);
      if (!inPool(currentId)) {
        // A restart under new filters is a fresh lap: the two clips picked at
        // mount were never watched, so don't hold them out of the rotation.
        served.clear();
        recent.clear();
        const fresh = pickRandom()?.id ?? null;
        setCurrentId(fresh);
        setNextId(pickRandom(fresh ?? undefined)?.id ?? null);
        setHistoryIds([]);
      } else if (!inPool(nextId)) {
        setNextId(pickRandom(currentId ?? undefined)?.id ?? null);
      }
    }
    setIntroLeaving(true);
    setTimeout(() => setIntroGone(true), ANIM_MS);
  }, [pool, pickRandom, currentId, nextId, served, recent]);

  // Bring the intro back — the only route to the filters once it's been
  // dismissed, which matters when the filters have emptied the feed.
  const reopenIntro = useCallback(() => {
    introDismissing.current = false;
    setIntroLeaving(false);
    setIntroGone(false);
  }, []);

  // The intro's scrollable region (the filters). Gestures that start in there
  // belong to it, not to dismiss-the-modal — otherwise you can't scroll the
  // filter list or tap a chip without the modal sliding away.
  const inIntroScroller = (target: EventTarget | null) =>
    target instanceof Element && !!target.closest('.feed-intro-scroll');

  // On desktop a "swipe up" is a trackpad/wheel scroll, not a drag — the feed
  // itself advances on wheel (see onWheel below). The intro layer sits over the
  // viewport as a sibling, so wheel events here reach neither handler unless we
  // add our own. Non-passive so preventDefault can stop the surrounding
  // scroll-snap page from hijacking the gesture, same reason as the feed's.
  useEffect(() => {
    const el = introLayerRef.current;
    if (!el || !showIntro) return;
    const onIntroWheel = (e: WheelEvent) => {
      if (inIntroScroller(e.target)) return; // let the filters scroll
      e.preventDefault();
      if (e.deltaY > 20) dismissIntro();
    };
    el.addEventListener('wheel', onIntroWheel, { passive: false });
    return () => el.removeEventListener('wheel', onIntroWheel);
  }, [showIntro, dismissIntro]);

  // Only upward movement counts; a downward pull just rubber-bands back.
  const introDragMove = useCallback((y: number) => {
    if (introDragFrom.current === null) return;
    const off = Math.min(0, y - introDragFrom.current);
    introOffsetRef.current = off;
    setIntroOffset(off);
  }, []);
  const introDragEnd = useCallback(() => {
    if (introDragFrom.current === null) return;
    introDragFrom.current = null;
    const travelled = introOffsetRef.current;
    introOffsetRef.current = 0;
    setIntroOffset(0);
    if (travelled < -60) dismissIntro();
  }, [dismissIntro]);

  const current = currentId ? techById.get(currentId) ?? null : null;
  const next = nextId ? techById.get(nextId) ?? null : null;
  const prevId = historyIds.length > 0 ? historyIds[historyIds.length - 1] : null;
  const prev = prevId ? techById.get(prevId) ?? null : null;

  // Drag state
  const [dragOffset, setDragOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  // Videos must start muted to satisfy mobile autoplay policy (an unmuted
  // autoplay is blocked, which also stalls videoReady and hides the scrubber).
  // The user enables sound with the speaker button — a real gesture — and the
  // choice persists across swipes for the rest of the session.
  const [soundOn, setSoundOn] = useState(false);
  const touchRef = useRef<{ y: number; t: number } | null>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  const getViewportH = () => viewportRef.current?.offsetHeight || 400;

  // ── Advance (swipe up → next) ──
  const commitAdvance = useCallback(() => {
    if (currentId) setHistoryIds((h) => [...h, currentId]);
    setCurrentId(nextId);
    setNextId(pickRandom(nextId ?? undefined)?.id ?? null);
    setDragOffset(0);
    setIsAnimating(false);
  }, [currentId, nextId, pickRandom]);

  // A guest out of free plays keeps the clip they're on and gets the wall, so
  // the last thing behind it is a video playing rather than a blank feed.
  // Every way to reach the next clip must ask: the button below, the swipe in
  // commitOrSnap, and the wheel step in onWheel are three separate paths.
  const guestBlocked = useCallback(() => {
    if (!guest || !previewExhausted()) return false;
    onPreviewExhausted?.();
    return true;
  }, [guest, onPreviewExhausted]);

  const advance = useCallback(() => {
    // Nothing queued: the swipe rubber-bands rather than advancing onto nothing.
    if (!nextId) return;
    if (guestBlocked()) return;
    setIsAnimating(true);
    setDragOffset(-getViewportH());
    setTimeout(commitAdvance, ANIM_MS);
  }, [nextId, commitAdvance, guestBlocked]);

  // Charge the allowance for whatever is on screen. Keyed on the id, and
  // recordPreviewPlay is idempotent per technique, so swiping back through
  // history costs nothing.
  useEffect(() => {
    if (!guest || !currentId) return;
    recordPreviewPlay(currentId);
  }, [guest, currentId]);

  // Listen for programmatic advance (used by demo loop)
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const handler = () => advance();
    el.addEventListener('feed-advance', handler);
    return () => el.removeEventListener('feed-advance', handler);
  }, [advance]);

  // ── Go back (swipe down → previous) ──
  const commitGoBack = useCallback(() => {
    setHistoryIds((h) => {
      const copy = [...h];
      const prevId = copy.pop()!;
      setNextId(currentId);
      setCurrentId(prevId);
      return copy;
    });
    setDragOffset(0);
    setIsAnimating(false);
  }, [currentId]);

  // ── Shared commit/snap logic ──
  const commitOrSnap = useCallback((dy: number, dt: number) => {
    const velocity = Math.abs(dy) / Math.max(dt, 1);
    const h = getViewportH();
    const threshold = Math.min(h * SWIPE_THRESHOLD_RATIO, SWIPE_THRESHOLD_MAX);

    // Nothing queued (a pool of one): an upward swipe snaps back like the
    // bottom of a list, rather than advancing onto an empty slide.
    if (dy < 0 && nextId && (-dy > threshold || (velocity > VELOCITY_THRESHOLD && dy < -30))) {
      if (guestBlocked()) {
        // Snap back under the wall rather than animating onto a slide the
        // guest isn't getting.
        setIsAnimating(true);
        setDragOffset(0);
        setTimeout(() => setIsAnimating(false), ANIM_MS);
        return;
      }
      setIsAnimating(true);
      setDragOffset(-h);
      setTimeout(commitAdvance, ANIM_MS);
    } else if (dy > 0 && historyIds.length > 0 && (dy > threshold || (velocity > VELOCITY_THRESHOLD && dy > 30))) {
      setIsAnimating(true);
      setDragOffset(h);
      setTimeout(commitGoBack, ANIM_MS);
    } else if (dy > 0 && historyIds.length === 0 && onExitTop && (dy > threshold || (velocity > VELOCITY_THRESHOLD && dy > 30))) {
      // Pulled down on the first item — hand control back to the page so it can
      // scroll up to the Discover intro.
      setIsAnimating(true);
      setDragOffset(0);
      setTimeout(() => setIsAnimating(false), ANIM_MS);
      onExitTop();
    } else {
      setIsAnimating(true);
      setDragOffset(0);
      setTimeout(() => setIsAnimating(false), ANIM_MS);
    }
  }, [historyIds.length, nextId, commitAdvance, commitGoBack, onExitTop, guestBlocked]);

  // ── Touch handlers ──
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (isAnimating) return;
    touchRef.current = { y: e.touches[0].clientY, t: Date.now() };
  }, [isAnimating]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchRef.current || isAnimating) return;
    const dy = e.touches[0].clientY - touchRef.current.y;
    setDragOffset((dy > 0 && historyIds.length === 0) || (dy < 0 && !nextId) ? dy * 0.15 : dy);
  }, [isAnimating, historyIds.length, nextId]);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchRef.current || isAnimating) return;
    const dy = e.changedTouches[0].clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;
    touchRef.current = null;
    commitOrSnap(dy, dt);
  }, [isAnimating, commitOrSnap]);

  // ── Mouse drag handlers (desktop) ──
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (isAnimating || (e.target as HTMLElement).closest('button, a')) return;
    e.preventDefault();
    touchRef.current = { y: e.clientY, t: Date.now() };
  }, [isAnimating]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!touchRef.current || isAnimating) return;
    const dy = e.clientY - touchRef.current.y;
    setDragOffset((dy > 0 && historyIds.length === 0) || (dy < 0 && !nextId) ? dy * 0.15 : dy);
  }, [isAnimating, historyIds.length, nextId]);

  const onMouseUp = useCallback((e: React.MouseEvent) => {
    if (!touchRef.current || isAnimating) return;
    const dy = e.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;
    touchRef.current = null;
    commitOrSnap(dy, dt);
  }, [isAnimating, commitOrSnap]);

  const onMouseLeave = useCallback(() => {
    if (!touchRef.current) return;
    touchRef.current = null;
    setIsAnimating(true);
    setDragOffset(0);
    setTimeout(() => setIsAnimating(false), ANIM_MS);
  }, []);

  // ── Scroll wheel (desktop) ──
  const wheelTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onWheel = useCallback((e: WheelEvent) => {
    // Always keep the gesture inside the feed. If we let any wheel event bubble
    // (e.g. while a go-back animation runs, or trackpad inertia after a step),
    // the surrounding scroll-snap page hijacks it and snaps back to the intro —
    // which is why scrolling up kept yanking you to the beginning.
    e.preventDefault();
    if (isAnimating) return;
    if (wheelTimeout.current) return; // debounce — one step per gesture
    if (e.deltaY > 30 && nextId) {
      if (guestBlocked()) return;
      wheelTimeout.current = setTimeout(() => { wheelTimeout.current = null; }, 400);
      const h = getViewportH();
      setIsAnimating(true);
      setDragOffset(-h);
      setTimeout(commitAdvance, ANIM_MS);
    } else if (e.deltaY < -30 && historyIds.length > 0) {
      wheelTimeout.current = setTimeout(() => { wheelTimeout.current = null; }, 400);
      const h = getViewportH();
      setIsAnimating(true);
      setDragOffset(h);
      setTimeout(commitGoBack, ANIM_MS);
    }
    // At the first video (no history) an upward scroll now does nothing instead
    // of exiting to the intro. Press Esc to return to the Discover intro.
  }, [isAnimating, historyIds.length, nextId, commitAdvance, commitGoBack, guestBlocked]);

  // Attach the wheel listener manually as non-passive so preventDefault() can
  // actually block the surrounding scroll-snap page — React's onWheel is
  // passive, which would let the page scroll to the intro on every scroll-up.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  // Same problem for touch: React's onTouchMove is passive, so without this the
  // surrounding scroll-snap page hijacks a downward swipe and snaps back to the
  // intro instead of letting the feed step to the previous video. Keep the
  // gesture inside the feed, except a downward pull at the first item (no
  // history) which should bubble up so the page can return to the intro.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onTouchMoveNative = (e: TouchEvent) => {
      if (!touchRef.current) return;
      const dy = e.touches[0].clientY - touchRef.current.y;
      if (dy > 0 && historyIds.length === 0 && onExitTop) return;
      e.preventDefault();
    };
    el.addEventListener('touchmove', onTouchMoveNative, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMoveNative);
  }, [historyIds.length, onExitTop]);

  // Escape returns to the Discover intro (main page view) when this feed lives
  // inside the Discover tab; for the standalone overlay feed it closes instead.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (onExitTop) onExitTop();
      else onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onExitTop, onClose]);

  // An empty pool is just an end-of-content state inside the normal feed shell,
  // not a different screen — and the intro (with its filters) still renders over
  // it, so the filters that emptied the feed are right there to loosen.
  const isEmpty = pool.length === 0;

  const player = (
    <div className={inline ? 'feed-player feed-player--inline' : 'feed-player'} onClick={inline ? undefined : (e) => e.stopPropagation()}>
      {isEmpty && (
        <>
          <PlayerHeader title="Nothing left to watch" onClose={inline ? undefined : onClose} />
          <div className="feed-empty">
            <p>That&rsquo;s everything we have for these filters.</p>
            {intro && (
              <button type="button" className="feed-empty-btn" onClick={reopenIntro}>
                Change filters
              </button>
            )}
          </div>
        </>
      )}
      <div
        ref={viewportRef}
        className="feed-viewport"
        hidden={isEmpty}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        {[prev, current, next].map((t, i) => {
          if (!t) return null;
          const slot = i === 0 ? 'prev' : i === 1 ? 'current' : 'next';
          const cn = slot === 'current' ? 'feed-current' : `feed-neighbor feed-neighbor--${slot}`;
          return (
            <div
              key={t.id}
              className={cn}
              style={{ transform: `translateY(${dragOffset}px)`, transition: isAnimating ? `transform ${ANIM_MS}ms cubic-bezier(0.4, 0, 0.2, 1)` : 'none' }}
            >
              {/* Header lives inside the slide so it scrolls away with its video. */}
              {/* An embed's speaker lives in the header row: down on the frame
                  it would sit on top of the player's own controls. */}
              <PlayerHeader
                title={t.name}
                subtitle={categories.find((c) => c.id === t.categoryId)?.label ?? ''}
                starred={statusMap[t.id] === TechniqueStatus.MarkedForPractice}
                onStar={() => onToggleStar(t.id)}
                onClose={inline ? undefined : onClose}
                // Above the YouTube gesture layer (3), which is a later sibling
                // spanning the whole slide: at the same z-index that layer won
                // on DOM order and swallowed every tap on the header's buttons,
                // so the speaker toggled playback instead of the sound.
                style={{ zIndex: 5 }}
                sourceUrl={t.instagramUrl}
                author={t.author}
                attribution={t.attribution}
                legality={legalityNote(t, rules)}
                techniqueId={t.id}
                soundOn={!allMuted && soundOn}
                onToggleSound={
                  slot === 'current' && !allMuted && detectPlatform(t.instagramUrl!) === 'youtube'
                    ? () => setSoundOn((v) => !v)
                    : undefined
                }
              />
              <FeedVideo
                url={t.instagramUrl!}
                thumbnail={t.thumbnail}
                onNext={slot === 'current' ? advance : undefined}
                muted={allMuted || !soundOn || slot !== 'current'}
                soundOn={!allMuted && soundOn}
                onToggleSound={slot === 'current' && !allMuted ? () => setSoundOn((v) => !v) : undefined}
                hold={showIntro}
                active={slot === 'current'}
              />
            </div>
          );
        })}
      </div>

      {/* Intro sheet over the first clip. Its own gesture handlers stop short of
          the viewport's, so dragging it doesn't also advance the feed. */}
      {showIntro && (
        <div
          ref={introLayerRef}
          className={`feed-intro-layer${introLeaving ? ' feed-intro-layer--leaving' : ''}`}
          onTouchStart={(e) => { e.stopPropagation(); if (!inIntroScroller(e.target)) introDragFrom.current = e.touches[0].clientY; }}
          onTouchMove={(e) => { e.stopPropagation(); introDragMove(e.touches[0].clientY); }}
          onTouchEnd={(e) => { e.stopPropagation(); introDragEnd(); }}
          onTouchCancel={introDragEnd}
          onMouseDown={(e) => { e.stopPropagation(); if (!inIntroScroller(e.target)) introDragFrom.current = e.clientY; }}
          onMouseMove={(e) => { if (introDragFrom.current !== null) { e.stopPropagation(); introDragMove(e.clientY); } }}
          onMouseUp={(e) => { e.stopPropagation(); introDragEnd(); }}
          onMouseLeave={introDragEnd}
        >
          <div
            className="feed-intro"
            style={introOffset ? { transform: `translateY(${introOffset}px)`, transition: 'none' } : undefined}
          >
            <div className="feed-intro-content">{intro}</div>
            {/* Swiping is the point, but the button says so out loud — a click
                has to work on desktop and for anyone using a keyboard. */}
            <div className="feed-intro-foot">
              <button className="feed-intro-start" onClick={dismissIntro}>
                Start watching
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (inline) return player;
  return <div className="feed-player-overlay" onClick={onClose}>{player}</div>;
}
